import { useUserInfo } from "@/api/user";
import {
  useDeletePendingInvite,
  useDeleteUser,
  useInviteUser,
  useRoles,
  useUpdateUser,
} from "@/api/users";
import {
  chipButtonClassName,
  GroupLabel,
  NESTED_DIALOG_Z,
  paneInputClassName,
  Row,
  SelectSm,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SelectItem } from "@/components/ui/select";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { OrganizationUser, PendingInvite, Role } from "@cohesive/api-client";
import { PersonAvatar } from "@cohesive/ui-components";
import { Copy, Mail, MoreHorizontal, UserRoundX, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

function formatInviteAge(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  if (Number.isNaN(ms) || ms < 60_000) return "Invited just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) {
    return `Invited ${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `Invited ${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  const days = Math.floor(hours / 24);
  return `Invited ${days} ${days === 1 ? "day" : "days"} ago`;
}

function roleLabel(roles: Role[] | undefined, roleIds: string[] | undefined) {
  if (!roleIds?.length || !roles?.length) return "—";
  return (
    roleIds
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "—"
  );
}

function isOwnerRole(roles: Role[] | undefined, roleIds: string[] | undefined) {
  const label = roleLabel(roles, roleIds);
  return /owner/i.test(label);
}

function MemberRoleSelect({
  user,
  roles,
  onUpdated,
}: {
  user: OrganizationUser;
  roles: Role[];
  onUpdated: () => void;
}) {
  const updateUser = useUpdateUser(user.id);
  const currentRoleId = user.auth0_role_ids?.[0] ?? "";

  return (
    <SelectSm
      borderless
      value={currentRoleId}
      onValueChange={async (roleId) => {
        try {
          await updateUser.mutateAsync({ auth0_role_ids: [roleId] });
          onUpdated();
          toast.success("The user has been successfully updated.");
        } catch {
          toast.error("Failed to update user role. Please try again.");
        }
      }}
      ariaLabel={`${user.name || user.email} role`}
      placeholder="Select role"
    >
      {roles.map((role) => (
        <SelectItem key={role.id} value={role.id}>
          {role.name}
        </SelectItem>
      ))}
    </SelectSm>
  );
}

function RemoveMemberDialog({
  user,
  open,
  onOpenChange,
  onRemoved,
}: {
  user: OrganizationUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemoved: () => void;
}) {
  const { mutateAsync: deleteUser, isPending } = useDeleteUser(user.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Remove member</DialogTitle>
          <DialogDescription>
            Remove {user.name || user.email} from this organization? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await deleteUser();
                onOpenChange(false);
                onRemoved();
                toast.success("The user has been successfully deleted.");
              } catch {
                toast.error(
                  "There was an error deleting the user. Please try again.",
                );
              }
            }}
          >
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelInviteDialog({
  invite,
  open,
  onOpenChange,
}: {
  invite: PendingInvite;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync: deletePendingInvite, isPending } =
    useDeletePendingInvite();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Cancel invitation</DialogTitle>
          <DialogDescription>
            Cancel the invitation for {invite.email}? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await deletePendingInvite(invite.id);
                onOpenChange(false);
                toast.success("The invitation has been successfully deleted.");
              } catch {
                toast.error(
                  "There was an error deleting the invitation. Please try again.",
                );
              }
            }}
          >
            Cancel invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface MembersPaneProps {
  users: OrganizationUser[];
  pendingInvites: PendingInvite[];
  seatLimit: number;
  canInvite: boolean;
  capacityAction?: React.ReactNode;
  onUserUpdated: () => void;
  onUserDeleted: () => void;
}

export function MembersPane({
  users,
  pendingInvites,
  seatLimit,
  canInvite,
  capacityAction,
  onUserUpdated,
  onUserDeleted,
}: MembersPaneProps) {
  const { data: userInfo } = useUserInfo();
  const { data: roles = [] } = useRoles();
  const inviteUser = useInviteUser();

  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [removeTarget, setRemoveTarget] = useState<OrganizationUser | null>(
    null,
  );
  const [cancelTarget, setCancelTarget] = useState<PendingInvite | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const { mutateAsync: deletePendingInvite } = useDeletePendingInvite();

  const emailValid = /.+@.+\..+/.test(email.trim());
  const canSubmitInvite =
    canInvite && emailValid && Boolean(roleId) && !inviteUser.isPending;

  const seatMeta = useMemo(() => {
    const used = users.length + pendingInvites.length;
    if (seatLimit <= 0) return null;
    return `${used} of ${seatLimit} seats`;
  }, [users.length, pendingInvites.length, seatLimit]);

  /**
   * Resend: there is no resend endpoint, so an invite is withdrawn and issued
   * again — a fresh email on a fresh expiry, which is what "resend" means to
   * the person waiting on it. Cancel first so the seat check can't trip on the
   * invite being replaced.
   */
  const resend = async (invite: PendingInvite) => {
    setResendingId(invite.id);
    try {
      await deletePendingInvite(invite.id);
      await inviteUser.mutateAsync({
        email: invite.email,
        auth0_role_ids: invite.auth0_role_ids,
        inviter_name: userInfo?.user?.name || userInfo?.user?.email,
      });
      toast.success(`Invitation resent to ${invite.email}.`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to resend the invitation. Please try again.",
      );
    } finally {
      setResendingId(null);
    }
  };

  const invite = async () => {
    if (!canSubmitInvite) return;
    try {
      await inviteUser.mutateAsync({
        email: email.trim(),
        auth0_role_ids: [roleId],
        inviter_name: userInfo?.user?.name || userInfo?.user?.email,
      });
      const selectedRole = roles.find((role) => role.id === roleId);
      analytics.trackUserInvited({
        targetUserEmail: email.trim(),
        targetUserRoles: [selectedRole?.name].filter(Boolean) as string[],
      });
      toast.success("User has been invited successfully.");
      setEmail("");
      setRoleId("");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to invite user. Please try again.",
      );
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-center gap-2 px-2.5">
        {canInvite ? (
          <>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void invite();
                }
              }}
              placeholder="Invite by email"
              type="email"
              autoComplete="email"
              className={cn(paneInputClassName, "max-w-72")}
              aria-label="Invite by email"
            />
            <SelectSm
              value={roleId}
              onValueChange={setRoleId}
              ariaLabel="Invite role"
              placeholder="Role"
              className="w-28"
            >
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectSm>
            <Button
              variant="secondary"
              size="sm"
              className={smButtonClassName}
              onClick={() => void invite()}
              disabled={!canSubmitInvite}
              isLoading={inviteUser.isPending}
            >
              Invite
            </Button>
          </>
        ) : (
          capacityAction
        )}
        <div className="flex-1" />
        {seatMeta ? (
          <span className="shrink-0 font-mono text-2xs text-n-40">
            {seatMeta}
          </span>
        ) : null}
      </div>

      <section className="flex flex-col gap-0.5">
        <GroupLabel>Members</GroupLabel>
        {users.map((user) => {
          const owner = isOwnerRole(roles, user.auth0_role_ids);
          const displayName = user.name || user.email;
          return (
            <Row
              key={user.id}
              frameless
              tile={
                <PersonAvatar
                  name={displayName}
                  email={user.email}
                  pictureUrl={user.picture_url}
                />
              }
              name={displayName}
              meta={user.email}
            >
              {owner ? (
                <span className="shrink-0 pr-2 text-sm text-n-50">
                  {roleLabel(roles, user.auth0_role_ids)}
                </span>
              ) : (
                <MemberRoleSelect
                  user={user}
                  roles={roles}
                  onUpdated={onUserUpdated}
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Manage ${displayName}`}
                    className="shrink-0 text-n-50 hover:text-n-90"
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onSelect={() => {
                      void navigator.clipboard.writeText(user.email);
                      toast.success("Email copied.");
                    }}
                  >
                    <Copy className="size-3.5 text-n-50" />
                    Copy email
                  </DropdownMenuItem>
                  {!owner ? (
                    <DropdownMenuItem
                      className="text-spectrum-red focus:text-spectrum-red data-[highlighted]:text-spectrum-red"
                      onSelect={() => {
                        window.setTimeout(() => setRemoveTarget(user), 0);
                      }}
                    >
                      <UserRoundX className="size-3.5" />
                      Remove from organization
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </Row>
          );
        })}
      </section>

      {pendingInvites.length > 0 ? (
        <section className="flex flex-col gap-0.5">
          <GroupLabel>Pending invites</GroupLabel>
          {pendingInvites.map((invite) => (
            <Row
              key={invite.id}
              dashed
              tile={<Mail className="size-4" />}
              name={invite.email}
              meta={formatInviteAge(invite.created_at)}
            >
              <Button
                variant="ghost"
                size="sm"
                className={cn("shrink-0", chipButtonClassName)}
                disabled={resendingId !== null}
                isLoading={resendingId === invite.id}
                onClick={() => void resend(invite)}
              >
                Resend
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Cancel invite to ${invite.email}`}
                className="shrink-0 text-n-50 hover:text-n-90"
                onClick={() => setCancelTarget(invite)}
              >
                <X />
              </Button>
            </Row>
          ))}
        </section>
      ) : null}

      {removeTarget ? (
        <RemoveMemberDialog
          user={removeTarget}
          open={Boolean(removeTarget)}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
          onRemoved={() => {
            setRemoveTarget(null);
            onUserDeleted();
          }}
        />
      ) : null}

      {cancelTarget ? (
        <CancelInviteDialog
          invite={cancelTarget}
          open={Boolean(cancelTarget)}
          onOpenChange={(open) => {
            if (!open) setCancelTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}
